/**
 * LogBuffer — fixed-capacity ring buffer for capturing process stdout/stderr lines.
 * When the buffer is full, the oldest lines are evicted (FIFO).
 * @docLink packages/connectors/api-reference#log-buffer
 */

export class LogBuffer {
  private readonly lines: string[] = [];
  private readonly capacity: number;

  constructor(capacity = 500) {
    this.capacity = capacity;
  }

  /** Append a single line. */
  push(line: string): void {
    this.lines.push(line);
    if (this.lines.length > this.capacity) {
      this.lines.shift();
    }
  }

  /** Split text on newlines and push each line (preserves empty lines). */
  pushLines(text: string): void {
    const lines = text.split("\n");
    // Avoid pushing a trailing empty string from "text\n".split("\n")
    for (let i = 0; i < lines.length; i++) {
      if (i === lines.length - 1 && lines[i] === "") break;
      this.push(lines[i]!);
    }
  }

  /** Return the last `n` lines. */
  tail(n: number): string[] {
    return this.lines.slice(-n);
  }

  /** Clear all stored lines. */
  clear(): void {
    this.lines.length = 0;
  }

  /** Current number of stored lines. */
  get size(): number {
    return this.lines.length;
  }
}
